import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { formatAccountingAmount, downloadCsv } from '../../../utils/formatters';
import { EmptyRecordsState } from '../../common/ResponsiveRecordList';

/**
 * Renders the exact shape returned by accounting-apis TrialBalanceService:
 *   { period_name, lines: [{account_code, account_name, class_name,
 *     type_name, sub_type_name, debit_balance, credit_balance, _row_type}],
 *     total_debit, total_credit, is_balanced, generated_at }
 * `_row_type: 'subtotal'` rows carry a pre-formatted label in account_name
 * (e.g. "Total Assets") - everything else is a normal posting line.
 */
const TrialBalanceReport = ({ data, loading }) => {
  if (loading) {
    return <Typography color="text.secondary">Generating trial balance...</Typography>;
  }
  if (!data) {
    return <EmptyRecordsState message="Choose a period and generate the report." />;
  }

  const lines = Array.isArray(data.lines) ? data.lines : [];

  const handleExport = () => {
    const headers = ['Code', 'Account', 'Class', 'Type', 'Sub Type', 'Debit', 'Credit'];
    const rows = lines.map((l) => [
      l.account_code || '',
      l.account_name || '',
      l.class_name || '',
      l.type_name || '',
      l.sub_type_name || '',
      l.debit_balance || 0,
      l.credit_balance || 0,
    ]);
    rows.push(['', 'TOTAL', '', '', '', data.total_debit || 0, data.total_credit || 0]);
    downloadCsv(`trial-balance-${data.period_name || 'report'}.csv`, headers, rows);
  };

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={1} sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h6">Trial Balance</Typography>
          <Typography variant="body2" color="text.secondary">{data.period_name}</Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip
            icon={data.is_balanced ? <CheckCircleIcon /> : <ErrorIcon />}
            label={data.is_balanced ? 'Balanced' : 'Unbalanced'}
            color={data.is_balanced ? 'success' : 'error'}
            size="small"
          />
          <Button size="small" startIcon={<DownloadIcon />} onClick={handleExport} disabled={!lines.length}>
            Export CSV
          </Button>
        </Stack>
      </Stack>

      {!data.is_balanced && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Debits and credits differ by {formatAccountingAmount(Math.abs((data.total_debit || 0) - (data.total_credit || 0)))}.
        </Alert>
      )}

      {lines.length === 0 ? (
        <EmptyRecordsState message="No account balances for this period." />
      ) : (
        <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell sx={{ fontWeight: 700 }}>Code</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Account</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Debit</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Credit</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {lines.map((line, i) => {
                const isSubtotal = line._row_type === 'subtotal';
                return (
                  <TableRow
                    key={i}
                    hover={!isSubtotal}
                    sx={isSubtotal ? { bgcolor: 'action.selected' } : undefined}
                  >
                    <TableCell sx={{ color: 'text.secondary' }}>{line.account_code}</TableCell>
                    <TableCell sx={{ fontWeight: isSubtotal ? 700 : 400, fontStyle: isSubtotal ? 'italic' : 'normal' }}>
                      {line.account_name}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: isSubtotal ? 700 : 400, fontVariantNumeric: 'tabular-nums' }}>
                      {formatAccountingAmount(line.debit_balance, { blankZero: true })}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: isSubtotal ? 700 : 400, fontVariantNumeric: 'tabular-nums' }}>
                      {formatAccountingAmount(line.credit_balance, { blankZero: true })}
                    </TableCell>
                  </TableRow>
                );
              })}
              <TableRow sx={{ bgcolor: 'primary.main' }}>
                <TableCell sx={{ color: 'primary.contrastText', fontWeight: 700 }} />
                <TableCell sx={{ color: 'primary.contrastText', fontWeight: 700 }}>TOTAL</TableCell>
                <TableCell align="right" sx={{ color: 'primary.contrastText', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                  {formatAccountingAmount(data.total_debit)}
                </TableCell>
                <TableCell align="right" sx={{ color: 'primary.contrastText', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                  {formatAccountingAmount(data.total_credit)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default TrialBalanceReport;
